import React from 'react';
import { ThemeProvider } from 'styled-components';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { BrowserRouter } from 'react-router-dom';
import { IntlProvider } from 'react-intl';
import { AppThemeProvider } from '@contexts/ThemeContext';
import { useTheme } from '@contexts/useTheme';
import { SnackbarProvider } from '@contexts/SnackbarContext';
import { DropDownMenuProvider } from '@contexts/DropDownMenuContext';
import { SidebarProvider } from '@contexts/SidebarContext';
import { AppsFooterProvider } from '@components/AppsFooter';
import { PageTitleProvider } from '@contexts/PageTitleContext';
import { DROP_DOWN_MENU_CONTAINER_ID } from '@components/2-molecules/DropDownMenuPortal';
import { AuthProvider } from '@contexts/AuthContext';
import { FeatureFlagProvider } from '@featureFlags/index';
import AppModelViewerProvider from '@components/ModelViewer/AppModelViewerProvider';
import enMessages from './intl/en';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30000, // 30 seconds
      gcTime: 5 * 60 * 1000, // 5 minutes (formerly cacheTime)
    },
  },
});

/**
 * Bridges the AppThemeProvider context into styled-components' ThemeProvider.
 */
const StyledThemeBridge: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { theme } = useTheme();
  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};

export const Providers: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <QueryClientProvider client={queryClient}>
      <IntlProvider locale="en" defaultLocale="en" messages={enMessages}>
        <BrowserRouter>
          <AppThemeProvider>
            <StyledThemeBridge>
              <SidebarProvider>
                <AppsFooterProvider>
                  <PageTitleProvider>
                    <DropDownMenuProvider>
                      <AuthProvider>
                        <FeatureFlagProvider>
                          <SnackbarProvider>
                            <AppModelViewerProvider>{children}</AppModelViewerProvider>
                            <div id={DROP_DOWN_MENU_CONTAINER_ID} />
                          </SnackbarProvider>
                        </FeatureFlagProvider>
                      </AuthProvider>
                    </DropDownMenuProvider>
                  </PageTitleProvider>
                </AppsFooterProvider>
              </SidebarProvider>
            </StyledThemeBridge>
          </AppThemeProvider>
        </BrowserRouter>
      </IntlProvider>
      {/* Bottom-right is the apps footer's controls. */}
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />}
    </QueryClientProvider>
  );
};
